import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "@store/index";
import { useGetDatasetQuestionsResponseMutation } from "@store/api/dataset-api";
import type {
  ScorecardDatasetMetricRow,
  ScorecardRollingAverageConfig,
  ScorecardTimePeriodSelection,
  ScorecardWeightSelection,
} from "@models/Scorecard";
import { resolveVisualizationBaseFilters } from "@utils/visualization-base-search";
import { resolveTimePeriodDates } from "@utils/time-period-utils";
import { metricBaseToVisualizationBase } from "@components/charts/scorecard-config/dataset-metric-row-utils";
import {
  extractCellFromData,
  getBaseFilterSignature,
  type ChartData,
  type ScorecardCellValue,
} from "@utils/scorecard-fetch-utils";

export type { ScorecardCellValue } from "@utils/scorecard-fetch-utils";

type RowCellMap = Record<string, ScorecardCellValue>;

/**
 * Live value fetching for Metric rows — mirrors useScorecardQuestionRowData
 * but always flat (Metric rows have no Existing-Row/crosstab-diagonal Base
 * concept, just their saved metric's own Base) and reads focus params/base
 * off the row's own cached fields (see dataset-metric-row-utils.ts's
 * buildDatasetMetricRowFetchFields) instead of live-deriving them, since the
 * DatasetMetric itself isn't re-fetched at render time. Metric population
 * (total_survey_population vs valid_question_respondents) is not yet applied
 * to the request — out of scope for this pass.
 */
export function useScorecardDatasetMetricRowData(
  datasetId: string | null,
  rows: ScorecardDatasetMetricRow[],
  weight: ScorecardWeightSelection = "DISABLED",
  timePeriodSelection: ScorecardTimePeriodSelection = {
    timePeriod: "all_time",
    startDate: null,
    endDate: null,
  },
  rollingAverage: ScorecardRollingAverageConfig = { enabled: false, windowSize: 2 },
  // When a data availability preview is active, the [start, end] range to apply to
  // an "all_time" selection (earliest response date → preview cutoff). All_time
  // otherwise sends no dates, so without this the preview ceiling is ignored for
  // scorecards; both dates are sent together so the range is never half-open.
  allTimePreviewRange: { startDate: string; endDate: string } | null = null,
) {
  const dispatch = useAppDispatch();
  const [getDatasetQuestionsResponse] = useGetDatasetQuestionsResponseMutation();

  // Keyed by `${sourceQuestionId}::${resolved filter signature}::${focus signature}`.
  const [dataByFetchKey, setDataByFetchKey] = useState<Record<string, ChartData>>({});
  const [loadingFetchKeys, setLoadingFetchKeys] = useState<Set<string>>(new Set());
  const [rowFetchKey, setRowFetchKey] = useState<Record<string, string>>({});
  const [resolvingRowIds, setResolvingRowIds] = useState<Set<string>>(new Set());

  // Fetches already fired, keyed by a date-INCLUSIVE fetchKey (see below), so an
  // identical request is never re-fired even if requestKey oscillates through a
  // transient during a preview change/end; the result stays cached in dataByFetchKey.
  const fetchedKeysRef = useRef<Set<string>>(new Set());
  // Resolved visualization-base filters cached per date-INDEPENDENT row config, so a
  // date/preview change re-fetches (new fetchKey) without re-resolving the filters.
  const resolvedFiltersRef = useRef<
    Map<string, Awaited<ReturnType<typeof resolveVisualizationBaseFilters>>>
  >(new Map());
  // Synchronous mirror of each row's current fetchKey — lets fireWithFilters bail
  // (no setState) when nothing changed, since the effect re-runs every render.
  const rowFetchKeyRef = useRef<Record<string, string>>({});
  // Config keys whose filter resolution is currently in flight — prevents
  // re-entering the async (and its setResolvingRowIds) on every render while the
  // resolve is still pending, which would otherwise loop synchronously forever.
  const resolvingConfigKeysRef = useRef<Set<string>>(new Set());

  const requestKey = [
    weight,
    timePeriodSelection.timePeriod,
    timePeriodSelection.startDate,
    timePeriodSelection.endDate,
    rollingAverage.enabled ? rollingAverage.windowSize : 0,
    allTimePreviewRange
      ? `${allTimePreviewRange.startDate}:${allTimePreviewRange.endDate}`
      : "",
  ].join("|");

  useEffect(() => {
    if (!datasetId) return;

    const resolvedPeriod = resolveTimePeriodDates(timePeriodSelection);
    const isAllTime = resolvedPeriod.timePeriod === "all_time";
    const shouldIncludeDates =
      !isAllTime && !!resolvedPeriod.startDate && !!resolvedPeriod.endDate;

    const commonParams = {
      weightSelection: weight,
      topN: null as number | null,
      sorting: "ORIGINAL" as const,
      ...(shouldIncludeDates && {
        startDate: resolvedPeriod.startDate!,
        endDate: resolvedPeriod.endDate!,
      }),
      // All Time under an active preview: apply the full [earliest, cutoff] range.
      ...(isAllTime &&
        allTimePreviewRange && {
          startDate: allTimePreviewRange.startDate,
          endDate: allTimePreviewRange.endDate,
        }),
      ...(rollingAverage.enabled &&
        shouldIncludeDates && { periodCount: rollingAverage.windowSize }),
    };

    rows.forEach((row) => {
      if (!row.sourceQuestionId) return;
      const sourceQuestionId = row.sourceQuestionId;

      // Date-independent key for caching the row's resolved base filters.
      const configKey = [
        row.id,
        sourceQuestionId,
        JSON.stringify(row.base),
        row.focusMode ?? "",
        row.metricValueCode ?? "",
        JSON.stringify(row.rollup ?? null),
        row.useTotalBase,
      ].join("::");

      const focusParams = {
        ...(row.focusMode && { focusMode: row.focusMode }),
        ...(row.metricValueCode && { focusEntity: row.metricValueCode }),
        ...(row.rollup && { focusRollup: row.rollup }),
      };

      const fireWithFilters = (
        filters: Awaited<ReturnType<typeof resolveVisualizationBaseFilters>>,
      ) => {
        // fetchKey is date-INCLUSIVE (requestKey): distinct dates are distinct
        // requests, identical ones are fetched once and served from cache.
        const fetchKey = [
          sourceQuestionId,
          getBaseFilterSignature(filters),
          JSON.stringify(focusParams),
          row.useTotalBase,
          requestKey,
        ].join("::");

        // Idempotent: if this row already points at this fetchKey there is nothing
        // to update or fetch — bail before any setState to avoid a render loop.
        if (rowFetchKeyRef.current[row.id] === fetchKey) return;
        rowFetchKeyRef.current = {
          ...rowFetchKeyRef.current,
          [row.id]: fetchKey,
        };
        setRowFetchKey((prev) => ({ ...prev, [row.id]: fetchKey }));

        if (fetchedKeysRef.current.has(fetchKey)) return;
        fetchedKeysRef.current.add(fetchKey);

        setLoadingFetchKeys((prev) => new Set(prev).add(fetchKey));
        getDatasetQuestionsResponse({
          id: sourceQuestionId,
          datasetId,
          chartType: "SCORECARD",
          filters: filters.length > 0 ? filters : undefined,
          ...focusParams,
          ...(row.useTotalBase && { useTotalBase: true }),
          ...commonParams,
        })
          .unwrap()
          .then((result) => {
            setDataByFetchKey((prev) => ({ ...prev, [fetchKey]: result.data ?? [] }));
          })
          .catch(() => {
            setDataByFetchKey((prev) => ({ ...prev, [fetchKey]: [] }));
          })
          .finally(() => {
            setLoadingFetchKeys((prev) => {
              const next = new Set(prev);
              next.delete(fetchKey);
              return next;
            });
          });
      };

      const cachedFilters = resolvedFiltersRef.current.get(configKey);
      if (cachedFilters) {
        fireWithFilters(cachedFilters);
        return;
      }

      // Resolve filters once per config; skip if already in flight so repeated
      // renders during the pending async don't re-trigger it (infinite loop).
      if (resolvingConfigKeysRef.current.has(configKey)) return;
      resolvingConfigKeysRef.current.add(configKey);
      setResolvingRowIds((prev) =>
        prev.has(row.id) ? prev : new Set(prev).add(row.id),
      );
      (async () => {
        const filters = await resolveVisualizationBaseFilters(
          dispatch,
          datasetId,
          metricBaseToVisualizationBase(row.base),
        );
        resolvedFiltersRef.current.set(configKey, filters);
        resolvingConfigKeysRef.current.delete(configKey);
        setResolvingRowIds((prev) => {
          if (!prev.has(row.id)) return prev;
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
        fireWithFilters(filters);
      })();
    });
  }, [
    datasetId,
    rows,
    getDatasetQuestionsResponse,
    requestKey,
    timePeriodSelection,
    rollingAverage,
    weight,
    allTimePreviewRange,
    dispatch,
  ]);

  const getRowCells = (row: ScorecardDatasetMetricRow): RowCellMap => {
    const cells: RowCellMap = {};
    const fetchKey = rowFetchKey[row.id];
    const data = fetchKey ? dataByFetchKey[fetchKey] : undefined;
    if (!data) return cells;
    row.brandMappings.forEach((mapping) => {
      if (!mapping.boundResponseLabel) return;
      const cell = extractCellFromData(data, mapping);
      if (cell) cells[mapping.brandId] = cell;
    });
    return cells;
  };

  const isRowLoading = (row: ScorecardDatasetMetricRow): boolean => {
    if (resolvingRowIds.has(row.id)) return true;
    const fetchKey = rowFetchKey[row.id];
    return !!fetchKey && loadingFetchKeys.has(fetchKey);
  };

  return { getRowCells, isRowLoading };
}
